import { get, send } from "../api.js";
import type { ActionDecision, BookingOfferings } from "./api.js";
import type { StaffLessonBookingResult } from "./staff.js";

export interface StaffOneOffBookingOffering {
  provider_id: number;
  service_id: number;
  service_name: string;
  available_credits: number;
  book_with_credit: ActionDecision;
  payment_pending: ActionDecision;
  default_preferred?: boolean;
}

export interface StaffOneOffBookingOfferings {
  portal_student_id: string;
  evaluated_at: string;
  offerings: StaffOneOffBookingOffering[];
}

export interface StaffTeachingAssignmentSetupResult {
  assignment_id: string;
  created: boolean;
}

const OFFERINGS_DEDUPE_MS = 5_000;
const offeringsRequests = new Map<
  string,
  { expiresAt: number; promise: Promise<BookingOfferings> }
>();

function clearOfferingsRequest(studentId: string) {
  for (const key of offeringsRequests.keys()) {
    if (key.startsWith(`${studentId}:`)) offeringsRequests.delete(key);
  }
}

function loadOfferings(studentId: string, providerId: number | null): Promise<BookingOfferings> {
  const requestKey = `${studentId}:${providerId ?? "all"}`;
  const now = Date.now();
  const existing = offeringsRequests.get(requestKey);
  if (existing && existing.expiresAt > now) return existing.promise;

  const promise = get<StaffOneOffBookingOfferings>(
    `/staff-api/api/v1/staff-booking/students/${encodeURIComponent(studentId)}/offerings`,
    providerId === null ? undefined : { provider_id: String(providerId) },
  ).then((result) => ({
    portal_student_id: result.portal_student_id,
    evaluated_at: result.evaluated_at,
    offerings: result.offerings.map((offering) => ({
      provider_id: offering.provider_id,
      service_id: offering.service_id,
      service_name: offering.service_name,
      default_preferred: offering.default_preferred,
      // Staff booking controls are dropdowns with an established deterministic
      // fallback. Family booking deliberately does not opt into this marker, so
      // multiple undifferentiated family offerings remain unselected.
      automatic_default_fallback: true,
      sources: [
        "hub_relationship" as const,
        ...(offering.book_with_credit.allowed ? (["lesson_credit"] as const) : []),
      ],
      available_credits: offering.available_credits,
      lesson_block_ids: [],
      book_with_credit: offering.book_with_credit,
      request_onsite: offering.payment_pending,
      assignment_setup_required: offering.payment_pending.reasons.some(
        (reason) => reason.code === "staff_assignment_setup_required",
      ),
    })),
  }));

  offeringsRequests.set(requestKey, {
    // Pending work never expires; give the next component a full reuse window
    // after the response arrives, even when Amelia takes longer than five seconds.
    expiresAt: Infinity,
    promise,
  });
  void promise.then(() => {
    const current = offeringsRequests.get(requestKey);
    if (current?.promise === promise) current.expiresAt = Date.now() + OFFERINGS_DEDUPE_MS;
  }).catch(() => {
    if (offeringsRequests.get(requestKey)?.promise === promise) {
      offeringsRequests.delete(requestKey);
    }
  });
  return promise;
}

export const tutorOneOffBooking = {
  invalidateOfferings: clearOfferingsRequest,
  /**
   * Server-owned provider/service and funding decisions for ordinary staff
   * booking. FastAPI resolves live Amelia services through the Hub teaching
   * assignment policy, so this still works when the student has zero credits.
   *
   * The setup gate and the canonical booking component both need the same
   * offerings snapshot during one drawer transition. Coalesce those adjacent
   * reads for a few seconds rather than making FastAPI repeat its Amelia and
   * Hub policy fan-out. Booking mutations are still revalidated server-side,
   * and teaching-assignment setup explicitly invalidates this short-lived read.
   *
   * A tutor-created student's first service is identified by the explicit
   * `staff_assignment_setup_required` policy reason. The adapter surfaces that
   * as presentation metadata only; it does not infer eligibility itself.
   */
  offerings: (studentId: string, providerId: number | null = null): Promise<BookingOfferings> =>
    loadOfferings(studentId, providerId),

  establishTeachingAssignment: async (
    studentId: string,
    body: { provider_id: number; service_id: number },
  ) => {
    const result = await send<StaffTeachingAssignmentSetupResult>(
      "POST",
      `/staff-api/api/v1/staff-booking/students/${encodeURIComponent(studentId)}/teaching-assignment`,
      body,
    );
    clearOfferingsRequest(studentId);
    return result;
  },

  /**
   * Explicit pay-later staff mutation. This creates a normal Amelia pending
   * on-site payment record; it is not a checkout or a payment-processing call.
   */
  paymentPendingBooking: (
    studentId: string,
    body: { provider_id: number; service_id: number; starts_at: string; notify_participants?: boolean },
    key: string,
    overrideWorkingHours = false,
  ) =>
    send<StaffLessonBookingResult>(
      "POST",
      `/staff-api/api/v1/staff-booking/students/${encodeURIComponent(studentId)}/appointments/payment-pending`,
      {
        ...body,
        notify_participants: body.notify_participants ?? false,
        override_working_hours: overrideWorkingHours,
      },
      { "Idempotency-Key": key },
    ),
};
